import * as React from 'react';

import { useLocalization } from '@shared-ui/localization-context';

import { PropertySource } from './property-source';

import { HotelViewModel } from 'models';

import { UitkCard, UitkCardContentSection } from 'uitk-react-cards';
import { UitkIcon, UitkIconSize } from 'uitk-react-icons';
import { UitkLayoutFlex, UitkLayoutFlexItem } from 'uitk-react-layout-flex';
import { UitkLayoutGrid, UitkLayoutGridItem } from 'uitk-react-layout-grid';
import { UitkLayoutPosition } from 'uitk-react-layout-position';
import { UitkSpacing } from 'uitk-react-spacing';
import { UitkText } from 'uitk-react-text';

import { isMemberOnlyDealListingFooter } from 'utils/listing-utils';

import { CardLink } from './card-link';
import { HighightedPropertyDetails } from './highlighted-property-details';
import { ListingAmenities } from 'components/search/listing/internals/listing-amenities';
import { ListingCommonSection } from './listing-common-section';
import { ListingErrorBoundary } from './listing-error-boundary';
import { ListingFooter } from './footer';
import { ListingName } from './listing-name';
import { ListingPropertyBadges } from './listing-property-badges';
import { ListingTravelAd } from './listing-travel-ad';
import { Neighborhood } from './neighborhood';
import { PriceAndSupportingMessagesContainer } from './price-and-supporting-messages-container';
import { PrimaryBadge } from './primary-badge';
import { SearchListingPlaceholderLarge } from './placeholders';
import { Stars } from './star-rating';
import { TertiaryBadge } from './tertiary-badge';
import { TitleHeading } from './title-heading';

interface HorizontalListingProps {
  className?: string;
  index?: number;
  loading?: boolean;
  model: HotelViewModel;
  defaultListingTopMarginClass?: string;
  stars?: number;
  showDismiss?: boolean;
  onCardDismiss?: () => void;
  // eslint-disable-next-line no-unused-vars
  getTitleComponent: (imageAspectRatio?: string, extraProps?: any) => any;
  imageAspectRatio?: string;
  'data-stid'?: string;
  cardLinkProps?: any;
  routeStoreGoBack?: () => void;
  listingClickHandler?: () => void;
  setItemRef?: () => void;
  mapListingView?: boolean;
}

export const HorizontalListing: React.FunctionComponent<HorizontalListingProps> = ({
  index,
  loading,
  model,
  className,
  stars,
  getTitleComponent,
  imageAspectRatio,
  'data-stid': dataStid,
  cardLinkProps,
  listingClickHandler,
  setItemRef,
  mapListingView,
  showDismiss,
  onCardDismiss,
}) => {
  const {
    amenities,
    listingFooter,
    name,
    neighborhood,
    pinnedDetails,
    price,
    propertyListingBadgeModel,
    propertySummary,
    propertySource,
    secondaryCardMessage,
    supportingMessages,
    thumbnail,
    recentlyViewed,
    propertyDetailsLink,
    starRatingText,
  } = model;
  const l10n = useLocalization();
  const listingWrapperProps = {
    className,
    'data-stid': dataStid || '',
    ref: setItemRef,
    tabIndex: -1,
  };
  listingWrapperProps.className += ' horizontal';

  const highlightedPropertyDetailsProps = {
    propertyDetails: propertySummary,
  };

  const propertySourceProps = propertySource;

  const secondary = propertyListingBadgeModel && propertyListingBadgeModel.secondary;
  const pinned = Boolean(pinnedDetails?.heading);
  const displayListingFooter = isMemberOnlyDealListingFooter(listingFooter) || !mapListingView;

  const horizontalPriceAndSupportingMessagesContainerProps = {
    ...{ secondaryCardMessage, supportingMessages, price, secondary },
    infositeUrl: propertyDetailsLink?.uri?.relativePath,
    viewSmall: false,
    wrapPrice: false,
    priceFontWeight: '600',
    hotelId: model.hotelId,
    recentlyViewed,
    index,
  };

  if (loading) {
    return (
      <li {...listingWrapperProps}>
        <SearchListingPlaceholderLarge />
      </li>
    );
  }

  return (
    <li {...listingWrapperProps}>
      <ListingName visuallyHidden={true} />
      <UitkCard>
        {!mapListingView && pinned && <TitleHeading data-stid="title-heading" headingInfo={pinnedDetails} />}
        <UitkLayoutGrid columns={3}>
          <UitkLayoutGridItem colSpan={1}>
            <UitkLayoutPosition type="relative">
              <UitkLayoutFlex blockSize="full_size">
                <ListingErrorBoundary thumbnail={thumbnail} name={name || ''}>
                  {getTitleComponent(imageAspectRatio)}
                </ListingErrorBoundary>
              </UitkLayoutFlex>
              <PrimaryBadge propertyListingBadgeModel={propertyListingBadgeModel} />
              <TertiaryBadge propertyListingBadgeModel={propertyListingBadgeModel} />
            </UitkLayoutPosition>
          </UitkLayoutGridItem>
          {showDismiss && (
            <UitkLayoutGridItem colSpan={1}>
              <UitkLayoutPosition type="absolute" position={{ right: 'three', top: 'three', zindex: 'layer3' }}>
                <button onClick={onCardDismiss} data-stid="dismiss-card" aria-label={l10n.formatText('hotels.searchResults.close.text')}>
                  <UitkIcon className="dismiss-card" name="close" size={UitkIconSize.MEDIUM} />
                </button>
              </UitkLayoutPosition>
            </UitkLayoutGridItem>
          )}
          <UitkLayoutGridItem colSpan={2}>
            <UitkCardContentSection className="listing-content">
              <UitkLayoutFlex direction="column" blockSize="full_size">
                <ListingPropertyBadges />
                <ListingName lineCount={2} />
                {starRatingText && (
                  <UitkSpacing padding={{ blockend: 'half' }}>
                    <UitkText size={300}>{starRatingText}</UitkText>
                  </UitkSpacing>
                )}
                <Stars stars={stars} />
                <PropertySource {...propertySourceProps} />
                <HighightedPropertyDetails {...highlightedPropertyDetailsProps} />
                <Neighborhood neighborhood={neighborhood} />
                <ListingAmenities amenities={amenities} />
                <UitkLayoutFlexItem>
                  <UitkLayoutGrid columns={3} alignContent="end">
                    <UitkLayoutGridItem colSpan={2} alignSelf="end">
                      <div>
                        <ListingTravelAd />
                        <ListingCommonSection />
                      </div>
                    </UitkLayoutGridItem>
                    <UitkLayoutGridItem colSpan={1} alignSelf="end" justifySelf="end">
                      <div data-stid="content-hotel-price">
                        <PriceAndSupportingMessagesContainer {...horizontalPriceAndSupportingMessagesContainerProps} />
                      </div>
                    </UitkLayoutGridItem>
                  </UitkLayoutGrid>
                </UitkLayoutFlexItem>
              </UitkLayoutFlex>
            </UitkCardContentSection>
          </UitkLayoutGridItem>
        </UitkLayoutGrid>
        {displayListingFooter && (
          <ListingFooter
            {...cardLinkProps}
            data-stid="listing-footer"
            listingFooter={listingFooter}
            listingClickHandler={listingClickHandler}
          />
        )}
        <CardLink {...cardLinkProps} index={index} listingClickHandler={listingClickHandler} />
      </UitkCard>
    </li>
  );
};
